import { NavigateRightThinIcon } from '@/app/icons/navigate-right-thin'
import { AdDecision } from '@/app/util/ads-endpoint'
import Avatar from '@/components/avatar'
import { IGif } from '@giphy/js-types'
import { getGifHeight } from '@giphy/js-util'
import { Gif, GifOverlayProps } from '@giphy/react-components'
import dynamic from 'next/dynamic'
import { SyntheticEvent } from 'react'

const mobileGridGap = 6
export const ctaSectionHeight = 54
export const ctaSectionHeightWithGap = ctaSectionHeight + mobileGridGap

const GifOverlay = dynamic(() => import('@/components/grid/overlay/gif-overlay'), { ssr: false })

type Props = {
    ad: AdDecision
    gif: IGif
    width: number
    isMobile: boolean
}

const Wrapper = ({
    clickUrl,
    isMobile,
    children,
}: {
    clickUrl: string
    isMobile: boolean
    children: React.ReactNode
}) => {
    if (isMobile) {
        return (
            <a
                href={clickUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute left-0 z-10 flex flex-col overflow-hidden rounded-t-sm md:w-[251px]"
            >
                {children}
            </a>
        )
    }
    return (
        <div className="group absolute left-0 flex cursor-pointer flex-col overflow-hidden rounded-t-sm md:z-10 md:w-[251px]">
            {children}
        </div>
    )
}

const ClickableAd = ({ ad, gif, width, isMobile }: Props) => {
    const { click_url, cta_text, impression_url } = ad
    const clickUrl = click_url?.startsWith('http') ? click_url : `https://${click_url}`

    const onClick = !isMobile
        ? (e: SyntheticEvent<HTMLElement, Event>) => {
              e.preventDefault()
              e.stopPropagation()
              window.open(clickUrl, '_blank')
          }
        : undefined

    return (
        <Wrapper clickUrl={clickUrl} isMobile={isMobile}>
            <Gif
                gif={gif}
                width={width}
                height={getGifHeight(gif, width)}
                overlay={
                    !isMobile ? (props: GifOverlayProps) => <GifOverlay {...props} hideAttribution={true} /> : undefined
                }
                hideAttribution
                borderRadius={0}
                noLink
                onGifClick={(_, e) => onClick?.(e)}
                onGifSeen={(_) => {
                    try {
                        if (impression_url) {
                            let impressionPixel = new Image()
                            impressionPixel.src = impression_url
                            impressionPixel.style.display = 'none'
                            window.document.body.appendChild(impressionPixel)
                        }
                    } catch (_e) {
                        console.error(_e)
                    }
                }}
            />
            <div
                className="flex h-[55px] w-full items-center gap-2 rounded-b-sm border-r-[1.5px] border-b-[1.5px] border-l-[1.5px] border-giphyDarkGrey bg-giphyBlack px-3 group-hover:bg-giphyWhite/6"
                onClick={onClick}
                role="button"
                aria-label={cta_text}
            >
                <Avatar user={gif.user} size={32} className="pointer-events-none" />
                <div className="font-bold underline [@media(max-width:336px)]:hidden">{cta_text}</div>
                <NavigateRightThinIcon className="ml-auto size-5 fill-giphyWhite" />
            </div>
        </Wrapper>
    )
}

export default ClickableAd
